import { Order } from "@/api.types";
import { useSession } from "@/services/auth0/session";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useOrderById } from "./orders";
import axios from "axios";

export const parcelSchema = z.object({
  weight: z.number().min(0, "Weight must be greater than 0"),
  dimensions: z.object({
    length: z.number().min(0, "Length must be greater than 0"),
    width: z.number().min(0, "Width must be greater than 0"),
    height: z.number().min(0, "Height must be greater than 0"),
  }),
  description: z.string().optional(),
  isFragile: z.boolean(),
  requiresCooling: z.boolean(),
});

export type ParcelSchema = z.infer<typeof parcelSchema>;

interface ParcelOperation {
  existingParcelId?: string;
  delete?: boolean;
  data?: {
    weight: number;
    dimensions: {
      length: number;
      width: number;
      height: number;
    };
    isFragile: boolean;
    description: string;
  };
}

export function useUpdateParcels(
  orderId: string,
  options?: {
    onSuccess?: () => void;
  }
) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  const { data: currentOrder } = useOrderById(orderId);

  return useMutation({
    mutationFn: async (parcels: z.infer<typeof parcelSchema>[]) => {
      if (!currentOrder) {
        throw new Error("Order not found");
      }

      // Create operations array for batch update
      const operations: ParcelOperation[] = parcels.map((parcel, index) => {
        const existingParcel = currentOrder.parcels[index];

        // If we have an existing parcel, it's either an update or delete
        if (existingParcel) {
          return {
            existingParcelId: existingParcel.id,
            data: {
              weight: Number(parcel.weight) || 0,
              dimensions: {
                length: Number(parcel.dimensions?.length) || 0,
                width: Number(parcel.dimensions?.width) || 0,
                height: Number(parcel.dimensions?.height) || 0,
              },
              isFragile: Boolean(parcel.isFragile),
              description: parcel.description ?? "",
            },
          };
        }

        // If no existing parcel, it's a new one
        return {
          data: {
            weight: Number(parcel.weight) || 0,
            dimensions: {
              length: Number(parcel.dimensions?.length) || 0,
              width: Number(parcel.dimensions?.width) || 0,
              height: Number(parcel.dimensions?.height) || 0,
            },
            isFragile: Boolean(parcel.isFragile),
            description: parcel.description ?? "",
          },
        };
      });

      // Add delete operations for removed parcels
      if (currentOrder.parcels.length > parcels.length) {
        const parcelsToDelete = currentOrder.parcels.slice(parcels.length);
        parcelsToDelete.forEach((parcel) => {
          operations.push({
            existingParcelId: parcel.id,
            delete: true,
          });
        });
      }

      const { data } = await axios.patch<{
        data: Order;
        metadata: any;
      }>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/${orderId}/parcels`,
        { operations },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      await queryClient.invalidateQueries({
        queryKey: ["orders"],
      });
      options?.onSuccess?.();
    },
  });
}
